import { formatISO } from 'date-fns'
import { EHealthProceduresTypes, IAdmissionRowSlots, IDiagnosisRowSlots, IHealthProcedures } from '~/interfaces'
import { getTimestamp } from '~/utils'

export const groupDiagnosis = (healthProcedures: IHealthProcedures[]) => {
  let events: IDiagnosisRowSlots[] = []

  healthProcedures.forEach(healthProcedure => {
    const { id, type, startDate } = healthProcedure
    const isDiagnosis = type === EHealthProceduresTypes.DIAGNOSIS
    const isDiagnosisBiopsy = type === EHealthProceduresTypes.DIAGNOSIS_BIOPSY
    const isDiagnosisAnatomopathologicalTest = type === EHealthProceduresTypes.DIAGNOSIS_ANATOMOPATHOLOGICAL_TEST

    if (isDiagnosisBiopsy || isDiagnosisAnatomopathologicalTest) return
    if (!isDiagnosis) return events.push(healthProcedure as IDiagnosisRowSlots)

    const diagnosisGroup = healthProcedures.filter(event => {
      return getTimestamp(event.startDate) === getTimestamp(startDate) && event.id !== id
    })

    events = [...events, { ...healthProcedure, healthProcedures: diagnosisGroup }]
  })

  return events
}

export const createAdmissionsSlotsFrom = (
  admissions: IHealthProcedures[],
  healthProcedures: IHealthProcedures[]
): IAdmissionRowSlots[] => {
  let slots: IAdmissionRowSlots[] = []

  admissions.forEach(admission => {
    const admissionStartDate = getTimestamp(admission.startDate)
    const admissionEndDate = getTimestamp(admission.endDate)

    const healthProceduresInPeriodOfTime = healthProcedures.filter(healthProcedure => {
      const healthProcedureStartDate = getTimestamp(healthProcedure.startDate)
      const healthProcedureEndDate = getTimestamp(healthProcedure.endDate)
      return healthProcedureStartDate >= admissionStartDate && healthProcedureEndDate <= admissionEndDate
    })

    slots = [...slots, { ...admission, healthProcedures: healthProceduresInPeriodOfTime }]
  })

  return slots
}

export const getAllEventsInsideAdmissions = (
  admissions: IHealthProcedures[],
  healthProcedures: IHealthProcedures[]
): IHealthProcedures[] => {
  const admissionSlots = createAdmissionsSlotsFrom(admissions, healthProcedures)
  const admissionsEvents = admissionSlots.flatMap(admission => admission.healthProcedures)

  return admissionsEvents
}

export const preventAdmissionsOutOfTimelineRange = (
  admissions: IAdmissionRowSlots[],
  timelineRange: Date[]
): IAdmissionRowSlots[] => {
  return admissions.map(admission => {
    const admissionStartDate = getTimestamp(admission.startDate)
    const admissionEndDate = getTimestamp(admission.endDate)

    const timelineRangeStartDate = getTimestamp(timelineRange[0])
    const timelineRangeEndDate = getTimestamp(timelineRange[1])

    const isAdmissionEarlierBoundary = admissionStartDate < timelineRangeStartDate
    const isAdmissionExcessBoundary = admissionEndDate > timelineRangeEndDate

    if (isAdmissionEarlierBoundary)
      return {
        ...admission,
        startDate: isAdmissionEarlierBoundary ? formatISO(timelineRange[0]) : admission.startDate
      }
    if (isAdmissionExcessBoundary) {
      return {
        ...admission,
        endDate: isAdmissionExcessBoundary ? formatISO(timelineRange[1]) : admission.endDate
      }
    }

    return admission
  })
}

export const removeHealthProceduresOfAdmissions = (
  healthProceduresWithoutAdmissions: IHealthProcedures[],
  admissions: IHealthProcedures[]
): IHealthProcedures[] => {
  let healthProceduresOutOfAdmissions: IHealthProcedures[] = []

  healthProceduresWithoutAdmissions.forEach(healthProcedure => {
    const healthProcedureStartDate = getTimestamp(healthProcedure.startDate)
    const healthProcedureEndDate = getTimestamp(healthProcedure.endDate)

    const isHealthProcedureIndependent = admissions.every(admission => {
      const admissionStartDate = getTimestamp(admission.startDate)
      const admissionEndDate = getTimestamp(admission.endDate)
      return healthProcedureStartDate < admissionStartDate || healthProcedureEndDate > admissionEndDate
    })

    if (isHealthProcedureIndependent) {
      healthProceduresOutOfAdmissions = [...healthProceduresOutOfAdmissions, healthProcedure]
    }
  })

  return healthProceduresOutOfAdmissions
}

const isTheSameHealthProcedure =
  (a: IHealthProcedures) =>
  (b: IHealthProcedures): boolean => {
    return a.id === b.id
  }

export const isHealthProceduresGroupsEquals = (groupA: IHealthProcedures[], groupB: IHealthProcedures[]): boolean => {
  if (groupA.length !== groupB.length) return false

  return groupA.every(healthProcedureA => {
    const compareWithFirstGroup = isTheSameHealthProcedure(healthProcedureA)
    return groupB.some(compareWithFirstGroup)
  })
}
